// Puente MiPAE en turno → PAE (Rápido/Detallado).
//
// `crearPaeDesdeTurno` (App.jsx) arranca un PAE precargado con lo que el
// enfermero YA documentó en el turno. Antes solo pasaba `contextoClinico(
// registro)` como un bloque de texto plano; este puente además:
//
//  1) mapea los antecedentes ya escritos en la evolución por sistema del
//     cierre de turno (`registro.evolucion`, shape de evolucionSistema.js)
//     a los campos CERRADOS de la Anamnesis del PAE Detallado — el usuario
//     no re-tipea lo que ya registró. Solo los campos que existen; nunca
//     inventa ni infiere.
//  2) enriquece `contextoClinico` con los hallazgos/notas de la evolución
//     por sistema, que `contextoClinico` no lee (solo mira las entradas de
//     la cronología, no `registro.evolucion`).
//
// Anti-PII: mismo patrón que `contextoClinico` — cada fragmento de texto
// libre pasa por `detectPii` antes de sumarse; si dispara, ESE fragmento se
// omite (nunca todo el bloque), para no perder el resto de información
// segura por un dato sensible en un solo campo.
//
// Puro: sin red, sin persistencia. App.jsx la llama antes de setFormData.

import { contextoClinico } from './registroTurno';
import { SISTEMAS, sistemaState, tieneContenido } from './evolucionSistema';
import { examenParaPae } from './examenFisico';
import { detectPii } from './piiDetect';

const SEXO_EVOLUCION_A_ANAMNESIS = { F: 'Femenino', M: 'Masculino' };

// Texto libre "limpio": recortado y sin PII (si dispara, se descarta entero
// — no hay forma segura de sub-limpiar un fragmento con PII detectada).
function limpio(texto) {
  const t = (texto || '').trim();
  if (!t || detectPii(t).hit) return '';
  return t;
}

// "HTA, DM2 diagnosticada hace 5 años" → ['HTA', 'DM2 diagnosticada hace 5
// años']. El campo de Anamnesis (`morbidos`) es un MultiSelect de catálogo
// cerrado + array — separar por comas conserva cada antecedente como su
// propio ítem, igual a como se tipeó en la evolución (texto libre allá).
// Cada ítem pasa su propio filtro anti-PII (no todo o nada).
function listaDesdeTexto(texto) {
  return (texto || '')
    .split(/[,;]/)
    .map((x) => x.trim())
    .filter(Boolean)
    .filter((x) => !detectPii(x).hit);
}

// Alergias: prioriza el detalle libre (si es seguro); si no hay detalle pero
// el enfermero marcó "tiene alergias", deja una señal sin inventar cuál.
// Si nunca se tocó el campo (tiene=false, sin detalle), no hay señal → ''.
function alergiasTexto(alergias) {
  const detalle = limpio(alergias?.detalle);
  if (detalle) return detalle;
  if (alergias?.tiene) return 'Alergias conocidas (sin detalle registrado en el turno)';
  return '';
}

// Campos de la Anamnesis del PAE Detallado (StepAnamnesis.jsx) que ya están
// documentados en la evolución por sistema del turno. Devuelve {} si no hay
// `registro.evolucion` o no aporta nada — SOLO incluye lo que existe.
export function anamnesisDesdeTurno(registro) {
  const evolucion = registro?.evolucion;
  if (!evolucion || typeof evolucion !== 'object') return {};

  const anamnesis = {};

  const dx = limpio(evolucion.antecedentes?.dx);
  if (dx) anamnesis.dx_medico = dx;

  const morbidos = listaDesdeTexto(evolucion.antecedentes?.morbidos);
  if (morbidos.length) anamnesis.morbidos = morbidos;

  const quirurgicos = limpio(evolucion.antecedentes?.quirurgicos);
  if (quirurgicos) anamnesis.quirurgicos = quirurgicos;

  const alergias = alergiasTexto(evolucion.alergias);
  if (alergias) anamnesis.alergias = alergias;

  const sexo = SEXO_EVOLUCION_A_ANAMNESIS[evolucion.sexo];
  if (sexo) anamnesis.sexo = sexo;

  return anamnesis;
}

// Hallazgos/notas de la evolución por sistema, ordenados por aparato — lo
// que `contextoClinico` no lee (solo mira las entradas de la cronología).
// Reusa `sistemaState` (evolucionSistema.js) para leer cada sistema de forma
// tolerante a claves ausentes, en vez de reimplementar el acceso. Devuelve
// '' si no hay evolución o no queda nada seguro que agregar.
function evolucionPorSistemaTexto(registro) {
  const evolucion = registro?.evolucion;
  if (!evolucion || !tieneContenido(evolucion)) return '';

  const bloques = [];
  for (const sis of SISTEMAS) {
    const { hallazgos, nota } = sistemaState(evolucion, sis.id);
    const halValidos = hallazgos.filter((h) => sis.opciones.includes(h));
    const notaLimpia = limpio(nota);
    if (!halValidos.length && !notaLimpia) continue;

    const nombre = sis.label.split(' · ')[0];
    const partes = [];
    if (halValidos.length) partes.push(halValidos.join(', '));
    if (notaLimpia) partes.push(notaLimpia.replace(/\.+$/, ''));
    bloques.push(`${nombre}: ${partes.join('; ')}`);
  }
  return bloques.length ? `Evolución por sistema: ${bloques.join('. ')}.` : '';
}

// Puente completo: { anamnesis, textoContexto }.
//  - anamnesis: prefill de StepAnamnesis (PAE Detallado), solo campos con dato.
//  - textoContexto: anamnesis breve pegada en el turno (si es segura) +
//    contextoClinico(registro) + examen físico de ingreso (si hay) +
//    evolución por sistema (si hay), para `libre.confirman` (Rápido) o
//    `anamnesis.evolucion` (Detallado).
export function paePrefillDesdeTurno(registro) {
  // La anamnesis breve del tablero (antecedentes.anamnesis) es la historia del
  // paciente que el usuario ya pegó — abre el contexto del PAE. Mismo criterio
  // que en el pulido: con PII detectada se omite entera, jamás a medias.
  const historia = limpio(registro?.evolucion?.antecedentes?.anamnesis);
  const base = contextoClinico(registro);
  // La FOTO de entrada alimenta la valoración diagnóstica (D-2, 2026-07-09):
  // antes el examen solo llegaba al PAE si el usuario apretaba "Traer a la
  // evolución" (y solo los hallazgos etiquetados). `examenParaPae` es PII-safe
  // por construcción — catálogo cerrado y números; la nota libre jamás viaja —
  // así que no necesita pasar por `limpio()`. Va ANTES de la evolución: en la
  // lectura clínica, primero cómo llegó, después cómo evolucionó.
  const examen = examenParaPae(registro?.examenFisico);
  const extra = evolucionPorSistemaTexto(registro);
  const textoContexto = [historia && `Anamnesis: ${historia.replace(/\.+$/, '')}.`, base, examen, extra]
    .filter((t) => t && t.trim())
    .join(' ');
  return { anamnesis: anamnesisDesdeTurno(registro), textoContexto };
}
